import axios from "axios";
import { getToken, refreshToken, logout } from "../users/AuthService";
import authEvents from "../users/AuthEvents";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (config.skipAuth) {
    return config;
  }

  const token = getToken();

  if (!token) {
    authEvents.emit("SESSION_EXPIRED");
    return config;
  }

  config.headers.Authorization = `Bearer ${token}`;

  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

let refreshTimeout = null;

const resetRefreshState = () => {
  isRefreshing = false;
  processQueue(new Error("Refresh timeout"), null);
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {

    if (error?.silent) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const status = error?.response?.status;
    const errorType = error?.response?.data?.type;

    if (status === 401 && errorType === "JWT_EXPIRED" && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;

      refreshTimeout = setTimeout(() => {
        resetRefreshState();
        authEvents.emit("SESSION_EXPIRED");
      }, 10000);

      try {
        await refreshToken();
        const newToken = getToken();

        if (!newToken) {
          throw new Error("Token não foi obtido após refresh");
        }

        if (refreshTimeout) {
          clearTimeout(refreshTimeout);
          refreshTimeout = null;
        }

        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);

      } catch (err) {

        if (refreshTimeout) {
          clearTimeout(refreshTimeout);
          refreshTimeout = null;
        }

        processQueue(err, null);
        logout();
        authEvents.emit("SESSION_EXPIRED");

        return Promise.reject(err);

      } finally {
        isRefreshing = false;
      }
    }

    if (status === 401) {
      logout();
      authEvents.emit("SESSION_EXPIRED");
      return Promise.reject({ silent: true, message: "Unauthorized" });
    }

    if (!error.response) {
      logout();
      authEvents.emit("SESSION_EXPIRED");
      return Promise.reject({ silent: true, message: "Network Error" });
    }

    return Promise.reject(error);
  }
);

export default api;
